import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import useAppToast from '@/hooks/useAppToast';
import {
  fetchNotificationList,
  fetchNotificationUnreadCounts,
  markNotificationsRead,
  retryNotifications,
} from '@/services/admin/notification-center';
import type {
  NotificationBulkResult,
  NotificationListQuery,
  NotificationTab,
} from '@/types/admin-notification-center';

const LIST_KEY = 'notification-center';
const COUNT_KEY = 'notification-center-unread';

/**
 * The site in the URL — 'medix' when the route has none, which is the single-tenant case.
 *
 * Exported because the page needs it too: the エラー詳細 download is a plain GET the page issues itself,
 * and re-deriving the site there would be two places deciding what the fallback is.
 */
export const useCurrentSite = (): string => {
  const { site } = useParams<{ site: string }>();
  return site ?? 'medix';
};

export const useNotificationList = (query: NotificationListQuery) => {
  const site = useCurrentSite();

  return useQuery({
    queryKey: [LIST_KEY, site, query],
    queryFn: () => fetchNotificationList(site, query),
    // The list is a feed: a stale page that still offers 再送信 on a row someone else already fixed is worse
    // than a refetch. Kept short rather than zero so paging back and forth is not a round trip each way.
    staleTime: 10_000,
    placeholderData: previous => previous,
  });
};

export const useNotificationUnreadCounts = () => {
  const site = useCurrentSite();

  return useQuery({
    queryKey: [COUNT_KEY, site],
    queryFn: () => fetchNotificationUnreadCounts(site),
    staleTime: 10_000,
  });
};

type BulkAction = 'markRead' | 'retry';

const RUNNERS: Record<
  BulkAction,
  (site: string, ids: number[], tab: NotificationTab) => Promise<NotificationBulkResult>
> = {
  markRead: markNotificationsRead,
  retry: retryNotifications,
};

/**
 * Every bulk button goes through here, so all three report the same way.
 *
 * The toast quotes what ACTUALLY happened, not what was asked for: 「20件中 14件を再送信しました (6件は対象外)」.
 * Pressing 再送信 with a mixed selection is the normal case on this screen — a reviewer ticks a page and
 * hits the button — and a plain 「送信しました」 there would be a quiet lie about six rows.
 */
export const useNotificationBulkAction = () => {
  const site = useCurrentSite();
  const toast = useAppToast();
  const queryClient = useQueryClient();

  return useMutation({
    // `tab` decides whether the call goes to the API or to the mock — a tab that is not live yet must not
    // POST ids the server would read as belonging to a different inbox.
    mutationFn: ({
      action,
      ids,
      tab,
    }: {
      action: BulkAction;
      ids: number[];
      tab: NotificationTab;
      verb?: string;
    }) => RUNNERS[action](site, ids, tab),
    // `verb` lets the caller name the action the way its BUTTON does — the 審査 tab says 再取込, the other
    // two say 再送信. A toast that names a different action than the button just pressed reads as a bug.
    onSuccess: async (result, { action, verb }) => {
      // The remainder is named for what it IS, per action. 「対象外」 on a mark-read is wrong and reads as a
      // problem: those rows were simply already read, which is not an exception, it is the normal case when
      // someone ticks a whole page.
      const tail =
        result.skipped > 0
          ? action === 'markRead'
            ? `（${result.skipped}件は既に既読）`
            : `（${result.skipped}件は対象外）`
          : '';

      if (result.affected === 0) {
        toast.warning(
          action === 'markRead'
            ? '選択した通知はすべて既読です。'
            : `対象の通知がありませんでした。${tail}`
        );
      } else if (action === 'markRead') {
        toast.success(`${result.affected}件を既読にしました。${tail}`);
      } else {
        toast.success(
          `${result.affected}件を${verb ?? '再送信'}しました。${tail}`
        );

        // SAY WHAT THE CAP LEFT BEHIND, as its own toast rather than a longer first line.
        //
        // One press sends at most shinsa.sync.retry_max_records rezepts. Above that the rest simply stayed,
        // and until now nothing said so: the success toast counted the ROWS it acted on, which is a different
        // number, so 「1件を再送信しました」 was a true sentence sitting on top of a hundred rezepts nobody had
        // been told about. Same wording the older 同期通知 screen used, so an operator who knows one screen
        // reads the other.
        const remaining = result.remaining ?? 0;
        if (remaining > 0) {
          toast.info(
            `未同期のレセプトが${remaining}件残っています。もう一度実行してください。`
          );
        }
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [LIST_KEY] }),
        queryClient.invalidateQueries({ queryKey: [COUNT_KEY] }),
      ]);
    },
    onError: error => {
      if ((error as { response?: { status?: number } })?.response?.status === 401) return;
      toast.error('エラーが発生しました。');
    },
  });
};
